import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import moment from "moment-timezone";

pdfMake.vfs = (pdfFonts && pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) ||
    (pdfFonts && pdfFonts.vfs) ||
    pdfMake.vfs;

const COLORS = {
    ink: "#0a0a0a",
    text: "#1a1a1a",
    mute: "#6b7280",
    faint: "#9ca3af",
    border: "#e5e5e5",
    borderSoft: "#f0f0f0",
    panel: "#f9fafb",
    panelAlt: "#fafafa",
    completedBg: "#f0fdf4",
    completedFg: "#166534",
    cancelledBg: "#fef2f2",
    cancelledFg: "#991b1b",
    noshowBg: "#fffbeb",
    noshowFg: "#92400e",
    notCompletedBg: "#f0f0f0",
    notCompletedFg: "#71717a"
};

function statusPillColors(status) {
    switch (status) {
        case "COMPLETED":
            return { bg: COLORS.completedBg, fg: COLORS.completedFg };
        case "CANCELLED":
            return { bg: COLORS.cancelledBg, fg: COLORS.cancelledFg };
        case "NOSHOW":
            return { bg: COLORS.noshowBg, fg: COLORS.noshowFg };
        default:
            return { bg: COLORS.notCompletedBg, fg: COLORS.notCompletedFg };
    }
}

function statusLabel(status) {
    switch (status) {
        case "COMPLETED":
            return "Completed";
        case "CANCELLED":
            return "Cancelled";
        case "NOSHOW":
            return "No-show";
        default:
            return "Not Completed";
    }
}

function formatDateShort(iso) {
    if (!iso) return "";
    return moment.parseZone(iso).format("MMM D");
}

function formatTimeShort(iso) {
    if (!iso) return "";
    return moment.parseZone(iso).format("h:mm A");
}

function formatCompletion(value, fallbackIso) {
    if (!value || value === "NOT COMPLETED") return "—";
    return moment.parseZone(value || fallbackIso).format("MMM D, h:mm A");
}

function groupByProvider(rows) {
    const map = new Map();
    for (const r of rows) {
        const key = r.provider_id;
        if (!map.has(key)) {
            map.set(key, {
                providerId: key,
                firstname: r.provider_firstname,
                lastname: r.provider_lastname,
                email: r.provider_email,
                sessions: [],
                totalSessions: 0,
                completed: 0,
                cancelled: 0,
                noshows: 0,
                completedMinutes: 0,
                ratingSum: 0,
                ratingCount: 0
            });
        }
        const p = map.get(key);
        p.sessions.push(r);
        p.totalSessions += 1;
        if (r._rawStatus === "COMPLETED") {
            p.completed += 1;
            p.completedMinutes += r._minutes || 0;
        } else if (r._rawStatus === "CANCELLED") {
            p.cancelled += 1;
        } else if (r._rawStatus === "NOSHOW") {
            p.noshows += 1;
        }
        if (r._rating) {
            p.ratingSum += r._rating;
            p.ratingCount += 1;
        }
    }
    return Array.from(map.values()).sort((a, b) =>
        (a.lastname || "").localeCompare(b.lastname || "")
    );
}

function summaryCard(label, value, unit) {
    return {
        table: {
            widths: ["*"],
            body: [
                [
                    {
                        stack: [
                            {
                                text: label.toUpperCase(),
                                fontSize: 7,
                                color: COLORS.mute,
                                characterSpacing: 0.5,
                                margin: [0, 0, 0, 3]
                            },
                            {
                                text: unit
                                    ? [
                                          { text: String(value), fontSize: 16, bold: true, color: COLORS.ink },
                                          { text: ` ${unit}`, fontSize: 9, color: COLORS.faint }
                                      ]
                                    : [{ text: String(value), fontSize: 16, bold: true, color: COLORS.ink }]
                            }
                        ],
                        margin: [8, 6, 8, 6]
                    }
                ]
            ]
        },
        layout: {
            hLineColor: () => COLORS.border,
            vLineColor: () => COLORS.border,
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            paddingLeft: () => 0,
            paddingRight: () => 0,
            paddingTop: () => 0,
            paddingBottom: () => 0
        }
    };
}

function summaryRow(cards) {
    return {
        columns: cards.map((c) => ({ ...c, width: "*" })),
        columnGap: 8,
        margin: [0, 0, 0, 8]
    };
}

function providerBlock(p) {
    const hours = +(p.completedMinutes / 60).toFixed(1);
    const avgRating =
        p.ratingCount > 0
            ? +(p.ratingSum / p.ratingCount).toFixed(1)
            : null;

    const statsRight = [
        { text: String(p.totalSessions), bold: true, color: COLORS.ink, fontSize: 8 },
        { text: " sessions    ", color: COLORS.mute, fontSize: 8 },
        { text: String(p.completed), bold: true, color: COLORS.ink, fontSize: 8 },
        { text: " completed    ", color: COLORS.mute, fontSize: 8 },
        { text: String(hours), bold: true, color: COLORS.ink, fontSize: 8 },
        { text: " hrs", color: COLORS.mute, fontSize: 8 }
    ];
    if (avgRating !== null) {
        statsRight.push(
            { text: "    Rating: ", color: COLORS.mute, fontSize: 8 },
            { text: String(avgRating), bold: true, color: COLORS.ink, fontSize: 8 }
        );
    }

    // Provider name + headline stats, rendered as the first header row of the
    // sessions table (colSpan across all 6 columns). Keeping it inside the
    // table — rather than as a separate block above an `unbreakable` stack —
    // lets pdfMake repeat it on every page when a provider has more sessions
    // than fit on one page, instead of silently dropping the whole block.
    const providerHeaderRow = [
        {
            colSpan: 6,
            fillColor: COLORS.panel,
            margin: [0, 3, 0, 3],
            columns: [
                {
                    text: [
                        { text: `${p.firstname} ${p.lastname}`, bold: true, color: COLORS.ink, fontSize: 10 },
                        { text: `  ${p.email}`, color: COLORS.faint, fontSize: 8 }
                    ]
                },
                {
                    text: statsRight,
                    alignment: "right"
                }
            ]
        },
        {},
        {},
        {},
        {},
        {}
    ];

    const tableHeader = [
        { text: "DATE", fontSize: 7, bold: true, color: COLORS.mute, fillColor: COLORS.panelAlt },
        { text: "STATUS", fontSize: 7, bold: true, color: COLORS.mute, fillColor: COLORS.panelAlt },
        { text: "COMPLETED AT", fontSize: 7, bold: true, color: COLORS.mute, fillColor: COLORS.panelAlt },
        { text: "SERVICE", fontSize: 7, bold: true, color: COLORS.mute, fillColor: COLORS.panelAlt },
        { text: "LOCATION", fontSize: 7, bold: true, color: COLORS.mute, fillColor: COLORS.panelAlt },
        { text: "DURATION", fontSize: 7, bold: true, color: COLORS.mute, fillColor: COLORS.panelAlt, alignment: "right" }
    ];

    const sessionRows = p.sessions.map((s) => {
        const pill = statusPillColors(s._rawStatus);
        const locationText = [s.service_location_city, s.service_location_state]
            .filter(Boolean)
            .join(", ");
        return [
            {
                stack: [
                    { text: formatDateShort(s.booking_date), fontSize: 8, color: COLORS.text },
                    { text: formatTimeShort(s.booking_date), fontSize: 7, color: COLORS.faint }
                ]
            },
            {
                text: statusLabel(s._rawStatus),
                fontSize: 7,
                bold: true,
                color: pill.fg,
                fillColor: pill.bg,
                alignment: "center",
                margin: [2, 2, 2, 2]
            },
            {
                text:
                    s.booking_completion === "NOT COMPLETED"
                        ? "—"
                        : formatCompletion(s.booking_completion, s.booking_date),
                fontSize: 8,
                color: COLORS.text
            },
            {
                stack: [
                    { text: s.service_name, fontSize: 8, color: COLORS.text },
                    s.client_name
                        ? { text: `Client: ${s.client_name}`, fontSize: 7, color: COLORS.faint }
                        : { text: "" }
                ]
            },
            { text: locationText, fontSize: 8, color: COLORS.text },
            { text: `${s.service_duration} min`, fontSize: 8, color: COLORS.text, alignment: "right" }
        ];
    });

    const footerStats = [
        { text: "Total: ", color: COLORS.mute, fontSize: 8 },
        { text: String(p.totalSessions), bold: true, color: COLORS.ink, fontSize: 8 },
        { text: "   Completed: ", color: COLORS.mute, fontSize: 8 },
        { text: String(p.completed), bold: true, color: COLORS.ink, fontSize: 8 },
        { text: "   Hours: ", color: COLORS.mute, fontSize: 8 },
        { text: String(hours), bold: true, color: COLORS.ink, fontSize: 8 },
        { text: "   Cancelled: ", color: COLORS.mute, fontSize: 8 },
        { text: String(p.cancelled), bold: true, color: COLORS.ink, fontSize: 8 }
    ];
    if (p.noshows > 0) {
        footerStats.push(
            { text: "   No-shows: ", color: COLORS.mute, fontSize: 8 },
            { text: String(p.noshows), bold: true, color: COLORS.ink, fontSize: 8 }
        );
    }
    if (avgRating !== null) {
        footerStats.push(
            { text: "   Rating: ", color: COLORS.mute, fontSize: 8 },
            { text: `${avgRating} (${p.ratingCount})`, bold: true, color: COLORS.ink, fontSize: 8 }
        );
    }

    const footerRow = [
        {
            colSpan: 6,
            text: footerStats,
            alignment: "right",
            fillColor: COLORS.panel,
            margin: [0, 4, 0, 4]
        },
        {},
        {},
        {},
        {},
        {}
    ];

    // Single table: [provider header row][column header row][...sessions][totals].
    // headerRows: 2 repeats the provider name + column labels on continuation
    // pages. No `unbreakable`, so a provider with more sessions than one page
    // can break across pages instead of being dropped. dontBreakRows keeps an
    // individual session row from splitting across a page boundary.
    return {
        table: {
            headerRows: 2,
            dontBreakRows: true,
            widths: ["10%", "13%", "18%", "28%", "19%", "12%"],
            body: [providerHeaderRow, tableHeader, ...sessionRows, footerRow]
        },
        layout: {
            hLineColor: (i, node) => {
                const len = node.table.body.length;
                return i <= 2 || i >= len - 1
                    ? COLORS.border
                    : COLORS.borderSoft;
            },
            vLineColor: () => COLORS.border,
            hLineWidth: (i, node) => {
                const len = node.table.body.length;
                if (i === 0 || i === len) return 0.5; // outer top/bottom
                if (i === 1 || i === 2) return 0.5; // under provider + column headers
                if (i === len - 1) return 0.5; // above totals row
                return 0.25; // between session rows
            },
            vLineWidth: (i, node) =>
                i === 0 || i === node.table.widths.length ? 0.5 : 0,
            paddingLeft: () => 5,
            paddingRight: () => 5,
            paddingTop: () => 4,
            paddingBottom: () => 4
        },
        margin: [0, 0, 0, 12]
    };
}

export function buildPayrollDocDefinition({
    rows = [],
    summary,
    companyName,
    fromDate,
    toDate
}) {
    const providers = groupByProvider(rows);
    const fromLabel = moment(fromDate).format("MMMM D, YYYY");
    const toLabel = moment(toDate).format("MMMM D, YYYY");
    const generatedLabel = moment().format("MMM D, YYYY");

    const headerBlock = {
        columns: [
            {
                stack: [
                    {
                        text: "Provider Performance & Payroll Summary",
                        fontSize: 14,
                        bold: true,
                        color: COLORS.ink
                    },
                    {
                        text: `Report period: ${fromLabel} — ${toLabel}`,
                        fontSize: 9,
                        color: COLORS.mute,
                        margin: [0, 3, 0, 0]
                    }
                ]
            },
            {
                stack: [
                    companyName
                        ? {
                              text: companyName,
                              fontSize: 10,
                              bold: true,
                              color: COLORS.ink
                          }
                        : { text: "" },
                    {
                        text: `Generated ${generatedLabel}`,
                        fontSize: 9,
                        color: COLORS.mute,
                        margin: [0, 2, 0, 0]
                    }
                ],
                alignment: "right"
            }
        ],
        margin: [0, 0, 0, 16]
    };

    const headerDivider = {
        canvas: [
            {
                type: "line",
                x1: 0,
                y1: 0,
                x2: 515,
                y2: 0,
                lineWidth: 0.5,
                lineColor: COLORS.border
            }
        ],
        margin: [0, 0, 0, 14]
    };

    const summaryRow1 = summaryRow([
        summaryCard("Total Sessions", summary.totalSessions),
        summaryCard("Completed", summary.completed),
        summaryCard("Completed Hours", summary.completedHours, "hrs"),
        summaryCard("Completion Rate", summary.completionRate, "%")
    ]);

    const avgRatingValue =
        summary.avgRating !== null ? String(summary.avgRating) : "—";
    const avgRatingUnit =
        summary.ratingCount > 0 ? `(${summary.ratingCount})` : "";

    const summaryRow2 = summaryRow([
        summaryCard("Active Providers", summary.activeProviders),
        summaryCard(
            "Cancelled / No-shows",
            `${summary.cancelled} / ${summary.noshows}`
        ),
        summaryCard("Avg Rating", avgRatingValue, avgRatingUnit)
    ]);

    const sectionTitle = {
        text: "Provider Session Detail",
        fontSize: 11,
        bold: true,
        color: COLORS.ink,
        margin: [0, 16, 0, 0]
    };
    const sectionUnderline = {
        canvas: [
            {
                type: "line",
                x1: 0,
                y1: 0,
                x2: 515,
                y2: 0,
                lineWidth: 1.2,
                lineColor: COLORS.ink
            }
        ],
        margin: [0, 4, 0, 10]
    };

    const docDefinition = {
        pageSize: "LETTER",
        pageMargins: [40, 40, 40, 40],
        defaultStyle: { fontSize: 9, color: COLORS.text },
        content: [
            headerBlock,
            headerDivider,
            summaryRow1,
            summaryRow2,
            ...(providers.length > 0 ? [sectionTitle, sectionUnderline] : []),
            ...providers.map(providerBlock)
        ],
        footer: (currentPage, pageCount) => ({
            columns: [
                {
                    text: "MarketBox — Provider Performance & Payroll Summary",
                    fontSize: 8,
                    color: COLORS.faint,
                    margin: [40, 12, 0, 0]
                },
                {
                    text: `Page ${currentPage} of ${pageCount}`,
                    fontSize: 8,
                    color: COLORS.faint,
                    alignment: "right",
                    margin: [0, 12, 40, 0]
                }
            ]
        })
    };

    return docDefinition;
}

export function generatePayrollPDF(payload) {
    const docDefinition = buildPayrollDocDefinition(payload);
    pdfMake
        .createPdf(docDefinition)
        .download((payload && payload.filename) || "payroll-summary.pdf");
}
